import { useState, useEffect } from 'react';
import api from '../api/client';
import { PlusCircle, BookOpen, FileText, CheckSquare, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { QuestionGenerator } from '../components/QuestionGenerator';

type ActiveForm = 'course' | 'module' | 'lesson' | 'quiz' | null;

interface CourseOption {
  id: string;
  title: string;
}

interface ModuleOption {
  id: string;
  title: string;
}

export function TeacherDashboardPage() {
  
  const [active, setActive]    = useState<ActiveForm>(null);
  const [loading, setLoading]  = useState(false);

  // Dropdown data
  const [myCourses, setMyCourses]   = useState<CourseOption[]>([]);
  const [myModules, setMyModules]   = useState<ModuleOption[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedModuleId, setSelectedModuleId] = useState('');
  const [selectedLessonId, setSelectedLessonId] = useState('');
  const [myLessons, setMyLessons]               = useState<{id: string; title: string}[]>([]);

  // Form states
  const [courseForm, setCourseForm] = useState({
    title: '', description: '', subject: '',
    grade: '', board: 'CBSE', difficulty: 'INTERMEDIATE'
  });

  const [moduleForm, setModuleForm] = useState({
    title: '', description: '', estimatedMinutes: 45
  });

  const [lessonForm, setLessonForm] = useState({
    title: '', lessonType: 'TEXT',
    contentBlocks: [
      { type: 'heading',   text: '' },
      { type: 'paragraph', text: '' },
      { type: 'callout',   variant: 'info', text: '' },
    ]
  });

  const [quizTitle, setQuizTitle]   = useState('');
  const [questions, setQuestions]   = useState([
    {
      questionText: '', questionType: 'MCQ',
      options: [
        { id: 'A', text: '' }, { id: 'B', text: '' },
        { id: 'C', text: '' }, { id: 'D', text: '' },
      ],
      correctAnswer: 'A', explanation: '',
      bloomLevel: 'REMEMBER', difficulty: 'MEDIUM', marks: 1
    }
  ]);

  // Load teacher's courses on mount
  useEffect(() => {
    loadMyCourses();
  }, []);

  // Load modules when course is selected
  useEffect(() => {
    if (selectedCourseId) {
      loadModules(selectedCourseId);
    }
  }, [selectedCourseId]);

// Load lessons when module is selected
useEffect(() => {
  if (selectedModuleId) {
    api.get(`/api/v1/modules/${selectedModuleId}/lessons`)
      .then(res => {
        setMyLessons(res.data);
        setSelectedLessonId('');
      })
      .catch(() => {});
  } else {
    setMyLessons([]);
    setSelectedLessonId('');
  }
}, [selectedModuleId]);

  const loadMyCourses = async () => {
    try {
      const res = await api.get('/api/v1/courses');
      const courses = res.data.content || res.data || [];
      setMyCourses(courses.map((c: any) => ({
        id: c.id,
        title: c.title
      })));
    } catch {
      // silent
    }
  };

  const loadModules = async (courseId: string) => {
    try {
      const res = await api.get(`/api/v1/courses/${courseId}/modules`);
      setMyModules(res.data.map((m: any) => ({
        id: m.id,
        title: m.title
      })));
    } catch {
      // silent
    }
  };

  const createCourse = async () => {
    if (!courseForm.title || !courseForm.subject || !courseForm.grade) {
      toast.error('Fill in title, subject and grade');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/api/v1/courses', courseForm);
      toast.success('Course created! Publishing it now...');

      // Auto-publish the course
      await api.patch(`/api/v1/courses/${res.data.id}/publish`);
      toast.success('Course published and ready for students 🎉');

      // Refresh course list and select the new course
      await loadMyCourses();
      setSelectedCourseId(res.data.id);
      setActive(null);
      setCourseForm({
        title: '', description: '', subject: '',
        grade: '', board: 'CBSE', difficulty: 'INTERMEDIATE'
      });
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to create course');
    } finally {
      setLoading(false);
    }
  };

  const createModule = async () => {
    if (!selectedCourseId) {
      toast.error('Select a course first');
      return;
    }
    if (!moduleForm.title) {
      toast.error('Enter a module title');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/api/v1/modules', {
        courseId: selectedCourseId,
        ...moduleForm
      });
      toast.success('Module created! 🎉');

      // Refresh modules and select the new one
      await loadModules(selectedCourseId);
      setSelectedModuleId(res.data.id);
      setActive(null);
      setModuleForm({ title: '', description: '', estimatedMinutes: 45 });
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const createLesson = async () => {
    if (!selectedModuleId) {
      toast.error('Select a module first');
      return;
    }
    if (!lessonForm.title) {
      toast.error('Enter a lesson title');
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/v1/lessons', {
        moduleId: selectedModuleId,
        ...lessonForm,
        contentBlocks: lessonForm.contentBlocks.filter(
          b => b.text && b.text.trim()
        ),
        xpReward: 15,
        estimatedMins: 10,
      });
      toast.success('Lesson created! ✅');
      setActive(null);
      setLessonForm({
        title: '',
        lessonType: 'TEXT',
        contentBlocks: [
          { type: 'heading',   text: '' },
          { type: 'paragraph', text: '' },
          { type: 'callout',   variant: 'info', text: '' },
        ]
      });
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const createQuiz = async () => {
    if (!selectedCourseId) {
      toast.error('Select a course first');
      return;
    }
    if (!quizTitle) {
      toast.error('Enter a quiz title');
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/v1/assessments', {
        courseId: selectedCourseId,
        title: quizTitle,
        assessmentType: 'QUIZ',
        maxAttempts: 3,
        passScore: 60,
        shuffleQuestions: false,
        questions,
      });
      toast.success('Quiz created! 🎉');
      setActive(null);
      setQuizTitle('');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = `w-full bg-gray-800 border border-gray-700
    rounded-lg px-4 py-3 text-white text-sm
    focus:outline-none focus:border-green-400`;

  const labelClass = `block text-xs text-gray-400 mb-1`;

  const selectClass = `w-full bg-gray-800 border border-gray-700
    rounded-lg px-4 py-3 text-white text-sm
    focus:outline-none focus:border-green-400 cursor-pointer`;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <h1 className="text-2xl font-black text-white mb-1">
        Teacher Dashboard
      </h1>
      <p className="text-gray-400 text-sm mb-6">
        Create and manage your courses, lessons and quizzes
      </p>

      {/* ── Course and Module Selector ── */}
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 mb-6">
        <div className="text-xs font-bold text-green-400 mb-3 uppercase tracking-wider">
          Working Context
        </div>
        <div className="grid md:grid-cols-3 gap-4">

          {/* Course selector */}
          <div>
            <label className={labelClass}>Active Course</label>
            <select
              value={selectedCourseId}
              onChange={e => {
                setSelectedCourseId(e.target.value);
                setSelectedModuleId('');
              }}
              className={selectClass}
            >
              <option value="">— Select a course —</option>
              {myCourses.map(c => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          </div>

          {/* Module selector */}
          <div>
            <label className={labelClass}>Active Module</label>
            <select
              value={selectedModuleId}
              onChange={e => setSelectedModuleId(e.target.value)}
              disabled={!selectedCourseId}
              className={selectClass + (
                !selectedCourseId ? ' opacity-40 cursor-not-allowed' : ''
              )}
            >
              <option value="">— Select a module —</option>
              {myModules.map(m => (
                <option key={m.id} value={m.id}>{m.title}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Lesson selector */}
<div>
  <label className={labelClass}>Active Lesson</label>
  <select
    value={selectedLessonId}
    onChange={e => setSelectedLessonId(e.target.value)}
    disabled={!selectedModuleId}
    className={selectClass + (
      !selectedModuleId ? ' opacity-40 cursor-not-allowed' : ''
    )}
  >
    <option value="">— Select a lesson —</option>
    {myLessons.map(l => (
      <option key={l.id} value={l.id}>{l.title}</option>
    ))}
  </select>
</div>

        {/* Breadcrumb showing current context */}
        {(selectedCourseId || selectedModuleId) && (
          <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
            <span className="text-green-400">
              {myCourses.find(c => c.id === selectedCourseId)?.title || ''}
            </span>
            {selectedModuleId && (
              <>
                <ChevronRight size={10} />
                <span className="text-blue-400">
                  {myModules.find(m => m.id === selectedModuleId)?.title || ''}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Action Cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          {
            id: 'course', icon: BookOpen,
            label: 'New Course', color: 'text-blue-400',
            hint: 'Start here'
          },
          {
            id: 'module', icon: FileText,
            label: 'New Module', color: 'text-purple-400',
            hint: 'Select course first'
          },
          {
            id: 'lesson', icon: PlusCircle,
            label: 'New Lesson', color: 'text-green-400',
            hint: 'Select module first'
          },
          {
            id: 'quiz', icon: CheckSquare,
            label: 'New Quiz', color: 'text-yellow-400',
            hint: 'Select course first'
          },
        ].map(card => (
          <button
            key={card.id}
            onClick={() => setActive(
              active === card.id as ActiveForm
                ? null
                : card.id as ActiveForm
            )}
            className={`bg-gray-900 border rounded-xl p-4
              flex flex-col items-center gap-2 transition-all text-center
              ${active === card.id
                ? 'border-green-400/60 bg-green-400/5'
                : 'border-gray-800 hover:border-gray-700'
              }`}
          >
            <card.icon size={24} className={card.color} />
            <span className="text-sm font-semibold text-white">
              {card.label}
            </span>
            <span className="text-xs text-gray-600">{card.hint}</span>
          </button>
        ))}
      </div>

      {/* ── Create Course Form ── */}
      {active === 'course' && (
        <div className="bg-gray-900 border border-gray-800
                        rounded-2xl p-6 mb-4">
          <h2 className="text-lg font-bold text-white mb-4">
            Create New Course
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className={labelClass}>Course Title *</label>
              <input
                value={courseForm.title}
                onChange={e => setCourseForm(p => ({
                  ...p, title: e.target.value
                }))}
                placeholder="e.g. Class 12 Physics CBSE"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Subject *</label>
              <input
                value={courseForm.subject}
                onChange={e => setCourseForm(p => ({
                  ...p, subject: e.target.value
                }))}
                placeholder="Mathematics, Physics, Chemistry..."
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Grade *</label>
              <input
                value={courseForm.grade}
                onChange={e => setCourseForm(p => ({
                  ...p, grade: e.target.value
                }))}
                placeholder="10, 11, 12, UG..."
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Board</label>
              <select
                value={courseForm.board}
                onChange={e => setCourseForm(p => ({
                  ...p, board: e.target.value
                }))}
                className={selectClass}
              >
                {['CBSE','ICSE','State Board','IB','Cambridge','Other']
                  .map(b => (
                    <option key={b} value={b}>{b}</option>
                  ))
                }
              </select>
            </div>
            <div>
              <label className={labelClass}>Difficulty</label>
              <select
                value={courseForm.difficulty}
                onChange={e => setCourseForm(p => ({
                  ...p, difficulty: e.target.value
                }))}
                className={selectClass}
              >
                {['BEGINNER','INTERMEDIATE','ADVANCED'].map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Description</label>
              <textarea
                value={courseForm.description}
                onChange={e => setCourseForm(p => ({
                  ...p, description: e.target.value
                }))}
                rows={2}
                placeholder="What will students learn in this course?"
                className={inputClass + ' resize-none'}
              />
            </div>
          </div>
          <button
            onClick={createCourse}
            disabled={loading}
            className="mt-4 bg-green-400 text-gray-950 font-bold
                       px-6 py-2 rounded-lg text-sm hover:bg-green-300
                       disabled:opacity-50 transition-colors"
          >
            {loading ? 'Creating...' : 'Create and Publish Course'}
          </button>
        </div>
      )}

      {/* ── Create Module Form ── */}
      {active === 'module' && (
        <div className="bg-gray-900 border border-gray-800
                        rounded-2xl p-6 mb-4">
          <h2 className="text-lg font-bold text-white mb-1">
            Create New Module
          </h2>
          {!selectedCourseId && (
            <div className="text-yellow-400 text-xs mb-4 flex items-center gap-2">
              ⚠️ Select a course from the Working Context above first
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label className={labelClass}>Module Title *</label>
              <input
                value={moduleForm.title}
                onChange={e => setModuleForm(p => ({
                  ...p, title: e.target.value
                }))}
                placeholder="e.g. Chapter 1: Introduction to Algebra"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Description</label>
              <input
                value={moduleForm.description}
                onChange={e => setModuleForm(p => ({
                  ...p, description: e.target.value
                }))}
                placeholder="Brief overview of this module"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Estimated Minutes</label>
              <input
                type="number"
                value={moduleForm.estimatedMinutes}
                onChange={e => setModuleForm(p => ({
                  ...p, estimatedMinutes: Number(e.target.value)
                }))}
                className={inputClass}
              />
            </div>
          </div>
          <button
            onClick={createModule}
            disabled={loading || !selectedCourseId}
            className="mt-4 bg-green-400 text-gray-950 font-bold
                       px-6 py-2 rounded-lg text-sm hover:bg-green-300
                       disabled:opacity-50 transition-colors"
          >
            {loading ? 'Creating...' : 'Create Module'}
          </button>
        </div>
      )}

      {/* ── Create Lesson Form ── */}
      {active === 'lesson' && (
        <div className="bg-gray-900 border border-gray-800
                        rounded-2xl p-6 mb-4">
          <h2 className="text-lg font-bold text-white mb-1">
            Create New Lesson
          </h2>
          {!selectedModuleId && (
            <div className="text-yellow-400 text-xs mb-4 flex items-center gap-2">
              ⚠️ Select a module from the Working Context above first
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label className={labelClass}>Lesson Title *</label>
              <input
                value={lessonForm.title}
                onChange={e => setLessonForm(p => ({
                  ...p, title: e.target.value
                }))}
                placeholder="e.g. What is a Variable?"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Heading</label>
              <input
                value={lessonForm.contentBlocks[0].text}
                onChange={e => setLessonForm(p => {
                  const blocks = [...p.contentBlocks];
                  blocks[0] = { ...blocks[0], text: e.target.value };
                  return { ...p, contentBlocks: blocks };
                })}
                placeholder="Main heading shown at top of lesson"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Main Content</label>
              <textarea
                value={lessonForm.contentBlocks[1].text}
                onChange={e => setLessonForm(p => {
                  const blocks = [...p.contentBlocks];
                  blocks[1] = { ...blocks[1], text: e.target.value };
                  return { ...p, contentBlocks: blocks };
                })}
                rows={6}
                placeholder="Explain the concept clearly and simply. Students will read this as the main lesson content."
                className={inputClass + ' resize-none'}
              />
            </div>

            <div>
              <label className={labelClass}>
                Key Takeaway / Callout (optional)
              </label>
              <input
                value={lessonForm.contentBlocks[2].text}
                onChange={e => setLessonForm(p => {
                  const blocks = [...p.contentBlocks];
                  blocks[2] = { ...blocks[2], text: e.target.value };
                  return { ...p, contentBlocks: blocks };
                })}
                placeholder="e.g. Remember: A variable is a placeholder for an unknown number"
                className={inputClass}
              />
            </div>
          </div>
          <button
            onClick={createLesson}
            disabled={loading || !selectedModuleId}
            className="mt-4 bg-green-400 text-gray-950 font-bold
                       px-6 py-2 rounded-lg text-sm hover:bg-green-300
                       disabled:opacity-50 transition-colors"
          >
            {loading ? 'Creating...' : 'Create Lesson'}
          </button>
        </div>
      )}

      {/* ── Create Quiz Form ── */}
      {active === 'quiz' && (
        <div className="bg-gray-900 border border-gray-800
                        rounded-2xl p-6 mb-4">
          <h2 className="text-lg font-bold text-white mb-1">
            Create New Quiz
          </h2>
          {!selectedCourseId && (
            <div className="text-yellow-400 text-xs mb-4 flex items-center gap-2">
              ⚠️ Select a course from the Working Context above first
            </div>
          )}

          {/* AI Generator */}
          {selectedLessonId && (
            <div className="mb-6">
              <div className="text-xs text-gray-400 mb-2 font-semibold">
                OPTION 1 — Generate with AI
              </div>
              <QuestionGenerator
                lessonId={selectedLessonId}
                courseId={selectedCourseId}
                onQuestionsReady={(generated) => {
                  setQuestions(generated.map(q => ({
                    questionText: q.questionText,
                    questionType: q.questionType,
                    options: q.options,
                    correctAnswer: q.correctAnswer,
                    explanation: q.explanation,
                    bloomLevel: q.bloomLevel,
                    difficulty: q.difficulty,
                    marks: q.marks,
                  })));
                  toast.success('Questions loaded! Review below then create the quiz.');
                }}
              />
              <div className="text-xs text-gray-400 mt-4 mb-2 font-semibold">
                OPTION 2 — Write manually
              </div>
            </div>
          )}

          {/* Quiz title */}
          <div className="space-y-4 mb-6">
            <div>
              <label className={labelClass}>Quiz Title *</label>
              <input
                value={quizTitle}
                onChange={e => setQuizTitle(e.target.value)}
                placeholder="e.g. Chapter 1 Practice Quiz"
                className={inputClass}
              />
            </div>
          </div>

          {/* Questions */}
          {questions.map((q, qi) => (
            <div key={qi}
                 className="border border-gray-700 rounded-xl p-4 mb-4">
              <div className="text-xs text-gray-400 font-bold mb-3">
                Question {qi + 1}
              </div>
              <div className="space-y-3">
                <input
                  value={q.questionText}
                  onChange={e => setQuestions(prev => {
                    const qs = [...prev];
                    qs[qi] = { ...qs[qi], questionText: e.target.value };
                    return qs;
                  })}
                  placeholder="Type your question here..."
                  className={inputClass}
                />
                {q.options.map((opt, oi) => (
                  <div key={opt.id} className="flex gap-2 items-center">
                    <span className="text-gray-500 text-xs font-bold w-5">
                      {opt.id}.
                    </span>
                    <input
                      value={opt.text}
                      onChange={e => setQuestions(prev => {
                        const qs = [...prev];
                        const opts = [...qs[qi].options];
                        opts[oi] = { ...opts[oi], text: e.target.value };
                        qs[qi] = { ...qs[qi], options: opts };
                        return qs;
                      })}
                      placeholder={`Option ${opt.id}`}
                      className={inputClass}
                    />
                  </div>
                ))}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Correct Answer</label>
                    <select
                      value={q.correctAnswer}
                      onChange={e => setQuestions(prev => {
                        const qs = [...prev];
                        qs[qi] = { ...qs[qi], correctAnswer: e.target.value };
                        return qs;
                      })}
                      className={selectClass}
                    >
                      {['A','B','C','D'].map(o => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Difficulty</label>
                    <select
                      value={q.difficulty}
                      onChange={e => setQuestions(prev => {
                        const qs = [...prev];
                        qs[qi] = { ...qs[qi], difficulty: e.target.value };
                        return qs;
                      })}
                      className={selectClass}
                    >
                      {['EASY','MEDIUM','HARD'].map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <input
                  value={q.explanation}
                  onChange={e => setQuestions(prev => {
                    const qs = [...prev];
                    qs[qi] = { ...qs[qi], explanation: e.target.value };
                    return qs;
                  })}
                  placeholder="Explanation shown to student after answering"
                  className={inputClass}
                />
              </div>
            </div>
          ))}

          <button
            onClick={() => setQuestions(prev => [...prev, {
              questionText: '', questionType: 'MCQ',
              options: [
                { id: 'A', text: '' }, { id: 'B', text: '' },
                { id: 'C', text: '' }, { id: 'D', text: '' },
              ],
              correctAnswer: 'A', explanation: '',
              bloomLevel: 'REMEMBER', difficulty: 'MEDIUM', marks: 1
            }])}
            className="text-green-400 text-sm font-semibold mb-4
                       hover:text-green-300 transition-colors block"
          >
            + Add another question
          </button>

          <button
            onClick={createQuiz}
            disabled={loading || !selectedCourseId}
            className="w-full bg-green-400 text-gray-950 font-bold
                       py-3 rounded-lg text-sm hover:bg-green-300
                       disabled:opacity-50 transition-colors"
          >
            {loading ? 'Creating...' : 'Create Quiz'}
          </button>
        </div>
      )}

    </div>
  );
}